import React, { useState, useEffect, Suspense } from "react";
import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  increment,
  runTransaction,
  setDoc,
} from "firebase/firestore";
import { db } from "../utils/firebase";
import { useAuth } from "../hooks/AuthenticationProvider";
import MultiplayerMatchStats from "./MultiplayerMatchStats";
import { User, Trophy, X } from "lucide-react";
import Spinner from "./Spinner";

const MultiplayerGame = ({ matchId, gameComponent: GameComponent, onExit }) => {
  const { user, organizationId } = useAuth();
  const [match, setMatch] = useState(null);
  const [opponent, setOpponent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [finalStats, setFinalStats] = useState(null);

  useEffect(() => {
    if (!matchId || !organizationId || !user?.email) return;

    const matchRef = doc(
      db,
      `organizations/${organizationId}/pvpMatches/${matchId}`
    );

    // First, update your status in the match to "joined" if not already
    const updatePlayerStatus = async () => {
      try {
        const matchDoc = await getDoc(matchRef);
        if (matchDoc.exists()) {
          const matchData = matchDoc.data();

          // If you're in this match but haven't marked as joined yet
          if (matchData.players.includes(user.email)) {
            // Update to show you've joined the game
            await updateDoc(matchRef, {
              [`playerJoined.${user.email}`]: true,
              lastActive: serverTimestamp(),
            });

            console.log("Player marked as joined:", user.email);
          }
        }
      } catch (error) {
        console.error("Error updating player status:", error);
      }
    };

    updatePlayerStatus();

    const unsubscribe = onSnapshot(matchRef, async (docSnap) => {
      if (docSnap.exists()) {
        const matchData = docSnap.data();
        setMatch(matchData);

        // Check for player disconnections/quits
        if (matchData.gameState?.status === "active" && matchData.playerQuit) {
          const quittingPlayer = matchData.players.find(
            (p) => p !== user.email
          );
          handlePlayerQuit(quittingPlayer);
          return;
        }

        // Set opponent
        const opponentEmail = matchData.players.find((p) => p !== user.email);
        if (opponentEmail) {
          setOpponent({
            email: opponentEmail,
            name:
              matchData.playerNames?.[opponentEmail] ||
              opponentEmail.split("@")[0],
          });
        }

        // Special handling for game state transitions
        if (matchData.gameState) {
          // If game just became active, trigger the start
          if (matchData.gameState.status === "active" && !gameStarted) {
            setGameStarted(true);
          }

          // If game just completed, show results
          if (matchData.gameState.status === "completed") {
            setGameOver(true);
          }

          // Check if the other player joined too
          const playerJoined = matchData.playerJoined || {};
          const allPlayersJoined = matchData.players.every(
            (p) => playerJoined[p]
          );

          if (
            allPlayersJoined &&
            matchData.gameState.status === "ready" &&
            !gameStarted
          ) {
            console.log("✅ Both players joined and ready - activating game");
            await updateDoc(matchRef, {
              "gameState.status": "active",
              startTime: serverTimestamp(),
            });
          }

          if (
            allPlayersJoined &&
            matchData.gameState.status === "active" &&
            !gameStarted
          ) {
            console.log("All players joined, starting game");
            setGameStarted(true);
          }
        }

        setIsLoading(false);
      } else {
        // Match document deleted or doesn't exist
        onExit();
      }
    });

    // Setup interval to update last active timestamp
    const keepAliveInterval = setInterval(() => {
      updateDoc(matchRef, {
        lastActive: serverTimestamp(),
      }).catch((error) => console.error("Error updating lastActive:", error));
    }, 30000); // Every 30 seconds

    return () => {
      unsubscribe();
      clearInterval(keepAliveInterval);
    };
  }, [matchId, organizationId, user?.email, gameStarted]);

  useEffect(() => {
    if (match?.gameState?.status === "completed") {
      handleGameCompletion();
    }
  }, [match?.gameState?.status]);

  // This handles cases where a player quits mid-game
  const handlePlayerQuit = async (quittingPlayerEmail) => {
    if (!matchId || !organizationId || !user?.email) return;

    // Only proceed if the quitting player is not the current user
    if (quittingPlayerEmail === user.email) return;

    const matchRef = doc(
      db,
      `organizations/${organizationId}/pvpMatches/${matchId}`
    );
    const userRef = doc(
      db,
      `organizations/${organizationId}/users`,
      user.email
    );

    try {
      // Get the current match data
      const matchDoc = await getDoc(matchRef);
      if (!matchDoc.exists()) return;

      const matchData = matchDoc.data();
      const gameType = matchData.game;

      // Award win to the player who stayed
      const stayingPlayerEmail = user.email;

      // Calculate a fair score for the player who stayed
      // This is a simplified score - you can make it more sophisticated
      const fairScore = 100; // Base score for a forfeit win

      // Update match with results showing forfeit
      await updateDoc(matchRef, {
        gameState: {
          ...matchData.gameState,
          status: "completed",
          winner: stayingPlayerEmail,
          endTime: serverTimestamp(),
          forfeit: true,
          forfeitedBy: quittingPlayerEmail,
        },
      });

      // SIMPLIFIED: Update only essential fields in user document
      // (gameScore, lastPlayedGame, lastPlayedAt)
      await updateDoc(userRef, {
        gameScore: increment(fairScore),
        lastPlayedGame: gameType,
        lastPlayedAt: serverTimestamp(),
      });

      // Store individual match result in user's unified gameHistory subcollection
      try {
        const gameHistoryRef = doc(
          db,
          `organizations/${organizationId}/users/${user.email}/gameHistory/${matchId}`
        );

        await setDoc(gameHistoryRef, {
          matchId,
          gameType,
          mode: "multiplayer",
          opponent: {
            email: quittingPlayerEmail,
            name:
              matchData.playerNames?.[quittingPlayerEmail] ||
              quittingPlayerEmail.split("@")[0],
          },
          result: "win",
          reason: "forfeit",
          score: fairScore,
          opponentScore: 0,
          timestamp: serverTimestamp(),
          gameDuration: 0, // No real duration for forfeits
        });
      } catch (err) {
        console.log("Game history update error:", err);
      }

      // Automatically return to lobby
      onExit();
    } catch (error) {
      console.error("Error handling player quit:", error);
    }
  };

  const updateGameState = async (newState) => {
    if (!matchId || !organizationId || !match) return;

    const matchRef = doc(
      db,
      `organizations/${organizationId}/pvpMatches/${matchId}`
    );

    // Handle special case for progress updates
    if (newState.myProgress !== undefined) {
      // Create an object that stores each player's progress
      const updatedProgress = match.gameState.progress || {};
      updatedProgress[user.email] = newState.myProgress;

      await updateDoc(matchRef, {
        gameState: {
          ...match.gameState,
          progress: updatedProgress,
          // If score is also being updated
          ...(newState.myScore !== undefined
            ? {
                scores: {
                  ...match.gameState.scores,
                  [user.email]: newState.myScore,
                },
              }
            : {}),
          lastUpdated: serverTimestamp(),
        },
      });
      return;
    }

    // Handle regular state updates
    await updateDoc(matchRef, {
      gameState: {
        ...match.gameState,
        ...newState,
        lastUpdated: serverTimestamp(),
      },
    });
  };

  const submitScore = async (score, stats = null) => {
    setFinalStats(stats); // capture detailed game stats directly

    if (!matchId || !organizationId || !user?.email || !match) return;

    const matchRef = doc(
      db,
      `organizations/${organizationId}/pvpMatches/${matchId}`
    );
    const userRef = doc(
      db,
      `organizations/${organizationId}/users`,
      user.email
    );

    try {
      // Get the current state of the match
      const matchDoc = await getDoc(matchRef);
      if (!matchDoc.exists()) return;

      const currentMatch = matchDoc.data();
      const currentScores = currentMatch.gameState?.scores || {};
      const currentPlayerStats = currentMatch.gameState?.playerStats || {};

      // Create updated scores object
      const updatedScores = {
        ...currentScores,
        [user.email]: score,
      };

      // Create updated player stats object
      const updatedPlayerStats = {
        ...currentPlayerStats,
        [user.email]: stats || {},
      };

      // Check if both players have now submitted scores
      const allScoresSubmitted = match.players.every(
        (p) => p === user.email || updatedScores[p] !== undefined
      );

      // Update the document with new score, player stats, and possibly game status
      await updateDoc(matchRef, {
        "gameState.scores": updatedScores,
        "gameState.playerStats": updatedPlayerStats,
        ...(allScoresSubmitted
          ? { "gameState.status": "completed", endTime: serverTimestamp() }
          : {}),
      });

      // IMPORTANT: Add this to make sure gameScore is incremented immediately
      // even if handleGameCompletion is slow
      await updateDoc(userRef, {
        gameScore: increment(score),
      });
    } catch (error) {
      console.error("Error submitting score:", error);
    }
  };

  const endMatch = async () => {
    if (window.confirm("Are you sure you want to leave this game?")) {
      const matchRef = doc(
        db,
        `organizations/${organizationId}/pvpMatches/${matchId}`
      );

      // Check if we're in an active game
      if (match?.gameState?.status === "active") {
        // Mark that this player quit
        await updateDoc(matchRef, {
          playerQuit: user.email,
          endTime: serverTimestamp(),
        });
      } else {
        // If game hasn't started, just delete the match
        await deleteDoc(matchRef);
      }

      onExit();
    }
  };

  const handleGameCompletion = async () => {
    if (!matchId || !organizationId || !user?.email) return;

    const matchRef = doc(
      db,
      `organizations/${organizationId}/pvpMatches/${matchId}`
    );
    const userRef = doc(
      db,
      `organizations/${organizationId}/users`,
      user.email
    );

    try {
      // Get the final scores
      const matchDoc = await getDoc(matchRef);
      if (!matchDoc.exists()) return;

      const matchData = matchDoc.data();
      const scores = matchData.gameState.scores || {};
      const myScore = scores[user.email] || 0;
      const gameType = matchData.game;

      // Get player details and detailed game stats for each player
      const players = Object.keys(scores);
      const playerStats = matchData.gameState.playerStats || {};

      // Determine winner using a composite approach
      let winner = null;
      let highestScore = -1;
      let isDraw = false;

      // First pass: compare by primary score
      for (const player of players) {
        if (scores[player] > highestScore) {
          highestScore = scores[player];
          winner = player;
          isDraw = false;
        } else if (scores[player] === highestScore && scores[player] > 0) {
          // Potential tie - will be resolved with secondary metrics
          isDraw = true;
        }
      }

      // Second pass: if there's a tie, use game-specific metrics to break it
      if (isDraw && players.length === 2) {
        const player1 = players[0];
        const player2 = players[1];

        // Get detailed stats for both players if available
        const stats1 = playerStats[player1] || {};
        const stats2 = playerStats[player2] || {};

        // Game-specific tie-breaking logic
        switch (gameType) {
          case "color-clash":
            // For Stroop-like tasks, lower interference is better, then faster reaction time
            if (
              stats1.stroopInterference !== undefined &&
              stats2.stroopInterference !== undefined
            ) {
              winner =
                stats1.stroopInterference < stats2.stroopInterference
                  ? player1
                  : player2;
              isDraw = stats1.stroopInterference === stats2.stroopInterference;
            }
            // If still tied or no interference stats, check reaction time
            if (
              isDraw &&
              stats1.avgReactionTime !== undefined &&
              stats2.avgReactionTime !== undefined
            ) {
              winner =
                stats1.avgReactionTime < stats2.avgReactionTime
                  ? player1
                  : player2;
              isDraw = stats1.avgReactionTime === stats2.avgReactionTime;
            }
            break;

          case "inspection-time":
            // For Inspection Time, lower response time is better, then fewer errors
            if (
              stats1.averageResponseTime !== undefined &&
              stats2.averageResponseTime !== undefined
            ) {
              if (stats1.averageResponseTime !== stats2.averageResponseTime) {
                winner =
                  stats1.averageResponseTime < stats2.averageResponseTime
                    ? player1
                    : player2;
                isDraw = false;
              } else if (
                stats1.incorrectResponses !== undefined &&
                stats2.incorrectResponses !== undefined
              ) {
                winner =
                  stats1.incorrectResponses < stats2.incorrectResponses
                    ? player1
                    : player2;
                isDraw =
                  stats1.incorrectResponses === stats2.incorrectResponses;
              }
            }
            break;

          case "sustained-attention":
            // For Focus Finder (sustained attention), more hits, then fewer commission errors, then more stable reaction times
            if (
              stats1.hitCount !== undefined &&
              stats2.hitCount !== undefined
            ) {
              if (stats1.hitCount !== stats2.hitCount) {
                winner = stats1.hitCount > stats2.hitCount ? player1 : player2;
                isDraw = false;
              } else if (
                stats1.commissionErrors !== undefined &&
                stats2.commissionErrors !== undefined
              ) {
                if (stats1.commissionErrors !== stats2.commissionErrors) {
                  winner =
                    stats1.commissionErrors < stats2.commissionErrors
                      ? player1
                      : player2;
                  isDraw = false;
                } else if (
                  stats1.reactionTimeVariability !== undefined &&
                  stats2.reactionTimeVariability !== undefined
                ) {
                  winner = stats1.reactionTimeVariability;
                  stats2.reactionTimeVariability ? player1 : player2;
                  isDraw =
                    stats1.reactionTimeVariability ===
                    stats2.reactionTimeVariability;
                }
              }
            }
            break;

          case "nback":
            // For memory matrix, higher correct responses, then faster reaction time
            if (
              stats1.correctResponses !== undefined &&
              stats2.correctResponses !== undefined
            ) {
              if (stats1.correctResponses !== stats2.correctResponses) {
                winner =
                  stats1.correctResponses > stats2.correctResponses
                    ? player1
                    : player2;
                isDraw = false;
              } else if (
                stats1.averageReactionTime !== undefined &&
                stats2.averageReactionTime !== undefined
              ) {
                winner =
                  stats1.averageReactionTime < stats2.averageReactionTime
                    ? player1
                    : player2;
                isDraw =
                  stats1.averageReactionTime === stats2.averageReactionTime;
              }
            }
            break;

          case "set-shifting":
            // For rule shifter, more rules discovered, then fewer perseveration errors
            if (
              stats1.rulesDiscovered !== undefined &&
              stats2.rulesDiscovered !== undefined
            ) {
              if (stats1.rulesDiscovered !== stats2.rulesDiscovered) {
                winner =
                  stats1.rulesDiscovered > stats2.rulesDiscovered
                    ? player1
                    : player2;
                isDraw = false;
              } else if (
                stats1.perseverationErrors !== undefined &&
                stats2.perseverationErrors !== undefined
              ) {
                winner =
                  stats1.perseverationErrors < stats2.perseverationErrors
                    ? player1
                    : player2;
                isDraw =
                  stats1.perseverationErrors === stats2.perseverationErrors;
              }
            }
            break;

          case "reaction-time":
            // For speed reactor, faster average reaction time, then fewer missed targets
            if (
              stats1.avgReactionTime !== undefined &&
              stats2.avgReactionTime !== undefined
            ) {
              winner =
                stats1.avgReactionTime < stats2.avgReactionTime
                  ? player1
                  : player2;
              isDraw = stats1.avgReactionTime === stats2.avgReactionTime;
            } else if (
              stats1.bestReactionTime !== undefined &&
              stats2.bestReactionTime !== undefined
            ) {
              // If no average, use best reaction time
              winner =
                stats1.bestReactionTime < stats2.bestReactionTime
                  ? player1
                  : player2;
              isDraw = stats1.bestReactionTime === stats2.bestReactionTime;
            }
            // If still tied, check for missed stimuli/false starts
            if (
              isDraw &&
              stats1.missedStimuli !== undefined &&
              stats2.missedStimuli !== undefined
            ) {
              winner =
                stats1.missedStimuli < stats2.missedStimuli ? player1 : player2;
              isDraw = stats1.missedStimuli === stats2.missedStimuli;
            }
            break;

          case "category-fluency":
            // For category fluency, more unique items, then more clusters, then faster switches
            if (
              stats1.uniqueItemsGenerated !== undefined &&
              stats2.uniqueItemsGenerated !== undefined
            ) {
              if (stats1.uniqueItemsGenerated !== stats2.uniqueItemsGenerated) {
                winner =
                  stats1.uniqueItemsGenerated > stats2.uniqueItemsGenerated
                    ? player1
                    : player2;
                isDraw = false;
              } else if (
                stats1.clustersFormed !== undefined &&
                stats2.clustersFormed !== undefined
              ) {
                if (stats1.clustersFormed !== stats2.clustersFormed) {
                  winner =
                    stats1.clustersFormed > stats2.clustersFormed
                      ? player1
                      : player2;
                  isDraw = false;
                } else if (
                  stats1.averageSwitchSpeed !== undefined &&
                  stats2.averageSwitchSpeed !== undefined
                ) {
                  winner =
                    stats1.averageSwitchSpeed < stats2.averageSwitchSpeed
                      ? player1
                      : player2;
                  isDraw =
                    stats1.averageSwitchSpeed === stats2.averageSwitchSpeed;
                }
              }
            }
            break;

          case "data-stream":
            // For DSST (Data Stream), more correct responses, then faster reaction time
            if (
              stats1.correctResponses !== undefined &&
              stats2.correctResponses !== undefined
            ) {
              if (stats1.correctResponses !== stats2.correctResponses) {
                winner =
                  stats1.correctResponses > stats2.correctResponses
                    ? player1
                    : player2;
                isDraw = false;
              } else if (
                stats1.averageResponseTime !== undefined &&
                stats2.averageResponseTime !== undefined
              ) {
                winner =
                  stats1.averageResponseTime < stats2.averageResponseTime
                    ? player1
                    : player2;
                isDraw =
                  stats1.averageResponseTime === stats2.averageResponseTime;
              }
            }
            break;

          case "math-race":
            // For Math Race, higher correct responses, then lower average response time
            if (
              stats1.correctResponses !== undefined &&
              stats2.correctResponses !== undefined
            ) {
              if (stats1.correctResponses !== stats2.correctResponses) {
                winner =
                  stats1.correctResponses > stats2.correctResponses
                    ? player1
                    : player2;
                isDraw = false;
              } else if (
                stats1.averageResponseTime !== undefined &&
                stats2.averageResponseTime !== undefined
              ) {
                winner =
                  stats1.averageResponseTime < stats2.averageResponseTime
                    ? player1
                    : player2;
                isDraw =
                  stats1.averageResponseTime === stats2.averageResponseTime;
              }
            }
            break;

          case "span-task":
            // For Memory Span, longest sequence is better, then lower average response time
            if (
              stats1.longestSequence !== undefined &&
              stats2.longestSequence !== undefined
            ) {
              if (stats1.longestSequence !== stats2.longestSequence) {
                winner =
                  stats1.longestSequence > stats2.longestSequence
                    ? player1
                    : player2;
                isDraw = false;
              } else if (
                stats1.averageResponseTime !== undefined &&
                stats2.averageResponseTime !== undefined
              ) {
                winner =
                  stats1.averageResponseTime < stats2.averageResponseTime
                    ? player1
                    : player2;
                isDraw =
                  stats1.averageResponseTime === stats2.averageResponseTime;
              }
            }
            break;

          case "stop-signal":
            // For Stop Signal Task, higher final SSD, then lower SSRT
            if (
              stats1.finalSSD !== undefined &&
              stats2.finalSSD !== undefined
            ) {
              if (stats1.finalSSD !== stats2.finalSSD) {
                winner = stats1.finalSSD > stats2.finalSSD ? player1 : player2;
                isDraw = false;
              } else if (
                stats1.estimatedSSRT !== undefined &&
                stats2.estimatedSSRT !== undefined
              ) {
                winner =
                  stats1.estimatedSSRT < stats2.estimatedSSRT
                    ? player1
                    : player2;
                isDraw = stats1.estimatedSSRT === stats2.estimatedSSRT;
              }
            }
            // If still tied, check stop success vs. failure ratio
            if (
              isDraw &&
              stats1.stopSuccessCount !== undefined &&
              stats2.stopSuccessCount !== undefined &&
              stats1.stopFailCount !== undefined &&
              stats2.stopFailCount !== undefined
            ) {
              const ratio1 =
                stats1.stopSuccessCount /
                (stats1.stopSuccessCount + stats1.stopFailCount);
              const ratio2 =
                stats2.stopSuccessCount /
                (stats2.stopSuccessCount + stats2.stopFailCount);
              winner = ratio1 > ratio2 ? player1 : player2;
              isDraw = ratio1 === ratio2;
            }
            break;

          default:
            // For any other game, keep the primary score-based winner
            break;
        }

        // Final fallback - if still tied after all metrics, use the timestamp as a deterministic tiebreaker
        if (isDraw) {
          // Either use player ID or join timestamp for deterministic outcome
          winner = player1 < player2 ? player1 : player2; // Alphabetical order
          isDraw = false; // No more draws, we need a winner
        }
      }

      // Calculate game duration
      let gameDuration = 0;
      if (matchData.startTime && matchData.endTime) {
        gameDuration = Math.round(
          (matchData.endTime.toMillis() - matchData.startTime.toMillis()) / 1000
        );
      }

      // Get opponent info
      const opponentEmail = matchData.players.find((p) => p !== user.email);
      const opponentName =
        matchData.playerNames?.[opponentEmail] ||
        opponentEmail?.split("@")[0] ||
        "Unknown";
      const opponentScore = scores[opponentEmail] ?? 0;

      // Update match with results
      await updateDoc(matchRef, {
        gameState: {
          ...matchData.gameState,
          status: "completed",
          winner: isDraw ? "draw" : winner,
          endTime: serverTimestamp(),
          gameDuration,
        },
      });

      // ✅ Update ONLY the user's gameScore and activity timestamp
      try {
        await updateDoc(userRef, {
          gameScore: increment(myScore),
          lastPlayedGame: gameType,
          lastPlayedAt: serverTimestamp(),
        });
      } catch (err) {
        console.log("User score update error:", err);
      }

      // ✅ Save to gameHistory with detailed stats
      try {
        const gameHistoryRef = doc(
          db,
          `organizations/${organizationId}/users/${user.email}/gameHistory/${matchId}`
        );

        // Get any additional detailed stats that were submitted
        const detailedStats = finalStats || {};

        await setDoc(gameHistoryRef, {
          matchId,
          gameType,
          mode: "multiplayer",
          opponent: {
            email: opponentEmail,
            name: opponentName,
          },
          result: winner === user.email ? "win" : isDraw ? "draw" : "loss",
          score: myScore,
          opponentScore,
          timestamp: serverTimestamp(),
          gameDuration,
          // Include all the detailed game stats
          ...detailedStats,
          // Add forfeit info if applicable
          ...(matchData.playerQuit
            ? {
                reason: "forfeit",
                forfeitedBy: matchData.playerQuit,
              }
            : {}),
        });
      } catch (err) {
        console.log("Game history update error:", err);
      }

      // ✅ Schedule cleanup for completed match
      setTimeout(async () => {
        try {
          const finalMatchDoc = await getDoc(matchRef);
          if (
            finalMatchDoc.exists() &&
            finalMatchDoc.data()?.gameState?.status === "completed"
          ) {
            await deleteDoc(matchRef);
            console.log(`Deleted completed match: ${matchId}`);
          }
        } catch (err) {
          console.log("Match cleanup error:", err);
        }
      }, 5 * 60 * 1000); // 5 minutes
    } catch (error) {
      console.error("Error completing game:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-12">
        <Spinner />
        <p className="text-slate-300 mt-4">Loading multiplayer game...</p>
      </div>
    );
  }

  if (gameOver && match) {
    const myScore = match.gameState?.scores?.[user.email] || 0;
    const opponentScore = opponent
      ? match.gameState?.scores?.[opponent.email] || 0
      : 0;
    const iWon = myScore > opponentScore;
    const isTie = myScore === opponentScore;

    return (
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <div className="text-center mb-8">
          <div
            className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center ${
              iWon ? "bg-emerald-500" : isTie ? "bg-amber-500" : "bg-slate-500"
            }`}
          >
            <Trophy size={40} className="text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mt-4">
            {iWon ? "You Won!" : isTie ? "It's a Tie!" : "Game Over"}
          </h2>
          <p className="text-slate-300 mt-1">
            {iWon
              ? "Congratulations on your victory!"
              : isTie
              ? "Both players performed equally well."
              : "Better luck next time!"}
          </p>
        </div>

        <div className="flex justify-between items-center bg-slate-700 rounded-xl p-4 mb-6">
          <div className="text-center flex-1">
            <p className="text-slate-300 mb-1">Your Score</p>
            <p className="text-2xl font-bold text-white">{myScore}</p>
          </div>
          <div className="text-center px-4">
            <p className="text-slate-400 text-sm">VS</p>
          </div>
          <div className="text-center flex-1">
            <p className="text-slate-300 mb-1">
              {opponent ? opponent.name : "Opponent"}'s Score
            </p>
            <p className="text-2xl font-bold text-white">{opponentScore}</p>
          </div>
        </div>

        {/* Add the new Match Stats component here */}
        {opponent && (
          <MultiplayerMatchStats
            opponent={opponent}
            gameType={match.game}
            overrideMyStats={finalStats}
          />
        )}

        <div className="flex justify-center gap-4">
          <button
            onClick={onExit}
            className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            Return to Lobby
          </button>
        </div>
      </div>
    );
  }

  if (!GameComponent) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 text-center">
        <p className="text-white mb-4">Game component not found</p>
        <button
          onClick={onExit}
          className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
        >
          Return to Lobby
        </button>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col h-full">
      {/* Game Header */}
      <div className="flex items-center justify-between mb-4 bg-slate-800 p-3 rounded-lg">
        <div className="flex items-center gap-2">
          <User size={18} className="text-emerald-400" />
          <span className="text-white font-medium">
            You vs {opponent ? opponent.name : "Opponent"}
          </span>
        </div>

        <div className="flex gap-2">
          <button
            onClick={endMatch}
            className="p-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Game Area */}
      <div className="flex-1">
        <Suspense
          fallback={
            <div className="flex justify-center items-center h-64">
              <Spinner />
            </div>
          }
        >
          {match && (
            <GameComponent
              isMultiplayer={true}
              gameState={match.gameState}
              updateGameState={updateGameState}
              submitScore={submitScore}
              opponent={opponent}
            />
          )}
        </Suspense>
      </div>
    </div>
  );
};

export default MultiplayerGame;
